import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

/* Admin-only authenticator-app 2FA enrollment / disable. */
export default function MfaCard() {
    const [status, setStatus] = useState(null);
    const [uri, setUri] = useState("");
    const [code, setCode] = useState("");
    const [recovery, setRecovery] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/auth/mfa").then((r) => setStatus(r.data)).catch(() => setStatus({ available: false }));
    useEffect(() => {
        let cancelled = false;
        api.get("/auth/mfa").then((r) => !cancelled && setStatus(r.data)).catch(() => !cancelled && setStatus({ available: false }));
        return () => {
            cancelled = true;
        };
    }, []);

    async function begin() {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/mfa/setup");
            setUri(data.otpauth_uri);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    }

    async function confirm(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/mfa/confirm", { code: code.trim() });
            setRecovery(data.recovery_codes);
            setUri("");
            setCode("");
            toast.success("Two-factor authentication enabled");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    }

    async function disable(e) {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/mfa/disable", { code: code.trim() });
            setCode("");
            toast.success("Two-factor authentication disabled");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    }

    const inputCls = "h-11 w-44 rounded-md bg-zinc-950/60 border border-zinc-800 px-3 text-[15px] tracking-[0.25em] text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-500";

    return (
        <section data-testid="account-mfa-card" className="rounded-xl border border-zinc-800/80 bg-zinc-950/40 p-6 sm:p-7 space-y-5">
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                    <KeyRound className="h-4 w-4 text-zinc-300 stroke-[1.75]" />
                </div>
                <div>
                    <h2 className="text-base md:text-lg font-semibold text-zinc-100">Two-factor authentication</h2>
                    <p className="text-[13px] text-zinc-500 mt-0.5 leading-relaxed">
                        Admins see every client's data — protect this account with an authenticator app (Google Authenticator, 1Password, Authy).
                    </p>
                </div>
            </div>

            {status == null && <p className="text-[13px] text-zinc-500">Loading…</p>}
            {status && !status.available && <p className="text-[13px] text-amber-300/80" data-testid="mfa-unavailable">2FA isn't configured on this server yet (TOTP_ENCRYPTION_KEY / RECOVERY_PEPPER).</p>}

            {status?.available && !status.enabled && !uri && !recovery && (
                <button type="button" onClick={begin} disabled={busy} data-testid="mfa-begin-button"
                    className="h-10 px-4 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white inline-flex items-center gap-2 disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Set up two-factor authentication
                </button>
            )}

            {uri && (
                <form onSubmit={confirm} className="space-y-4" data-testid="mfa-enroll-form">
                    <p className="text-[13px] text-zinc-400">1. Scan this code with your authenticator app. 2. Enter the 6-digit code it shows.</p>
                    <div className="inline-block rounded-lg bg-white p-3" data-testid="mfa-qr">
                        <QRCodeSVG value={uri} size={180} />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)}
                            placeholder="123456" data-testid="mfa-enroll-code-input" className={inputCls} />
                        <button type="submit" disabled={busy || code.trim().length !== 6} data-testid="mfa-enroll-confirm-button"
                            className="h-11 px-4 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white disabled:opacity-60">
                            Confirm &amp; enable
                        </button>
                    </div>
                </form>
            )}

            {recovery && (
                <div className="rounded-lg border border-amber-800/40 bg-amber-950/20 p-4 space-y-2" data-testid="mfa-recovery-codes">
                    <p className="text-[13px] text-amber-200">Save these recovery codes now — each works once and they won't be shown again.</p>
                    <pre className="text-[13px] font-mono text-zinc-100 grid grid-cols-2 gap-x-6 gap-y-1">{recovery.map((c) => <span key={c}>{c}</span>)}</pre>
                    <button type="button" onClick={() => setRecovery(null)} data-testid="mfa-recovery-done-button" className="text-[12.5px] text-zinc-400 hover:text-zinc-100 underline underline-offset-4">I've saved them</button>
                </div>
            )}

            {status?.enabled && !recovery && (
                <form onSubmit={disable} className="space-y-3" data-testid="mfa-disable-form">
                    <p className="text-[13px] text-emerald-300/90" data-testid="mfa-enabled-status">
                        Enabled · {status.recovery_codes_left} recovery code{status.recovery_codes_left === 1 ? "" : "s"} left
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        <input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Current code"
                            data-testid="mfa-disable-code-input" className={inputCls} />
                        <button type="submit" disabled={busy || code.trim().length < 6} data-testid="mfa-disable-button"
                            className="h-11 px-4 rounded-md border border-zinc-700 bg-zinc-900 text-[13.5px] text-zinc-200 hover:bg-zinc-800 disabled:opacity-60">
                            Turn off 2FA
                        </button>
                    </div>
                </form>
            )}
        </section>
    );
}
